import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Store, Plus, Minus, Trash2, Truck, Home, MapPin,
  CreditCard, Wallet, Loader2, ArrowLeft, AlertCircle, ShoppingBag, X,
} from "lucide-react";
import PayPalPayModal from "../components/PayPalPayModal";
import PozzoTopNav from "../components/PozzoTopNav";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { SubPageNav } from "../components/SubPageNav";
import { QRDialog } from "../components/QRDialog";
import AppBottomBar from "../components/AppBottomBar";
import { fetchRestaurant } from "../lib/api";
import { T, tr } from "../lib/i18n";
import { tk, setCheckoutLang } from "../lib/checkoutI18n";
import { ConsentNote } from "../components/legal/LegalBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function ShopPage({ appMode = false }) {
  const nav = useNavigate();
  const [menu, setMenu] = useState({ categories: [], shipping: null, local_delivery: null });
  const [restaurant, setRestaurant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  setCheckoutLang(lang);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [step, setStep] = useState("menu");
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    mode: "pickup", address: "", notes: "",
    payment_method: "stripe",
  });
  const [submitting, setSubmitting] = useState(false);
  const [paypalPay, setPaypalPay] = useState(null);
  const [zoom, setZoom] = useState(null); // product being previewed in modal
  const [qrOpen, setQrOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { if (appMode) fetchRestaurant().then(setRestaurant).catch(() => {}); }, [appMode]);

  useEffect(() => {
    axios.get(`${API}/shop/menu`)
      .then((r) => setMenu(r.data || { categories: [] }))
      .catch(() => setMenu({ categories: [] }))
      .finally(() => setLoading(false));
  }, []);

  const cartLines = useMemo(() => Object.values(cart), [cart]);
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);
  const subtotal = cartLines.reduce((s, l) => s + l.item.price * l.qty, 0);
  const totalWeight = cartLines.reduce((s, l) => s + (l.item.weight_kg || 0) * l.qty, 0);
  const anyNoShip = cartLines.some((l) => l.item.no_shipping);
  const allNoShip = cartLines.length > 0 && cartLines.every((l) => l.item.no_shipping);

  const wineQty = cartLines.reduce(
    (s, l) => s + ((l.item.category || "").toLowerCase() === "vino" || (l.item.category || "").toLowerCase() === "vini" ? l.qty : 0),
    0,
  );
  const wineMinShort = wineQty > 0 && wineQty < 6 ? 6 - wineQty : 0;

  const shippingFee = useMemo(() => {
    if (!menu.shipping) return 0;
    if (form.mode === "pickup") return 0;
    if (form.mode === "shipping") {
      if (subtotal >= menu.shipping.free_threshold) return 0;
      if (totalWeight >= menu.shipping.heavy_kg_threshold) return menu.shipping.heavy_fee;
      return menu.shipping.standard_fee;
    }
    if (form.mode === "local_delivery" && menu.local_delivery) {
      if (subtotal >= menu.local_delivery.free_threshold) return 0;
      return menu.local_delivery.fee;
    }
    return 0;
  }, [form.mode, subtotal, totalWeight, menu]);

  const total = subtotal + shippingFee;

  // Auto-switch mode when cart composition changes.
  useEffect(() => {
    if (form.mode === "shipping" && anyNoShip) setForm((f) => ({ ...f, mode: "pickup" }));
    if (form.mode === "local_delivery" && !allNoShip) setForm((f) => ({ ...f, mode: "pickup" }));
  }, [anyNoShip, allNoShip]); // eslint-disable-line

  const addToCart = (it) => setCart((c) => {
    const cur = c[it.id]?.qty || 0;
    // Enforce stock cap (quadri stock=1).
    if (it.stock >= 0 && cur >= it.stock) return c;
    return { ...c, [it.id]: { item: it, qty: cur + 1 } };
  });
  const decItem = (id) => setCart((c) => {
    const cur = c[id]; if (!cur) return c;
    if (cur.qty <= 1) { const n = { ...c }; delete n[id]; return n; }
    return { ...c, [id]: { ...cur, qty: cur.qty - 1 } };
  });
  const remove = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const submit = async (e) => {
    e.preventDefault();
    if (!cartLines.length) return;
    if (wineMinShort > 0) {
      return alert(`${tk("wineCase")} ${wineMinShort}.`);
    }
    if (form.mode === "shipping" && !form.address.trim()) return alert(tk("enterShipAddress"));
    if (form.mode === "local_delivery" && !form.address.trim()) return alert(tk("enterAddress"));
    if (form.mode === "local_delivery" && subtotal < (menu.local_delivery?.min_order || 25))
      return alert(`${tk("minOrder")}: € ${menu.local_delivery?.min_order || 25}`);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/shop/orders`, {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        mode: form.mode, payment_method: form.payment_method,
        address: form.address.trim(), notes: form.notes.trim(),
        items: cartLines.map((l) => ({ item_id: l.item.id, qty: l.qty })),
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else if (form.payment_method === "paypal") setPaypalPay({ orderId: data.order_id, amount: data.total });
      else nav(`/shop/success?order_id=${data.order_id}&mode=${form.payment_method}`);
    } catch (err) {
      alert(err?.response?.data?.detail || tk("orderError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#faf3ee] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;
  }

  const cartButton = step === "menu" ? (
    <button
      onClick={() => setStep("checkout")} disabled={cartCount === 0}
      className="inline-flex items-center gap-2 rounded-full bg-[#f7ebdf] text-[#2b1410] px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
      data-testid="shop-open-cart"
    >
      <ShoppingBag className="h-4 w-4" />
      {tk("cart")} {cartCount > 0 && <span className="rounded-full bg-[#4a1c17] text-[#f7ebdf] px-1.5 py-0.5 text-[10px]">{cartCount}</span>}
    </button>
  ) : (
    <button
      onClick={() => setStep("menu")}
      className="inline-flex items-center gap-2 rounded-full bg-[#f7ebdf]/10 text-[#f7ebdf] px-3 py-1.5 text-sm font-semibold hover:bg-[#f7ebdf]/20"
      data-testid="shop-back-to-menu"
    >
      <ArrowLeft className="h-4 w-4" />
      Torna allo shop
    </button>
  );

  return (
    <div className="min-h-screen bg-[#faf3ee] pb-32" data-testid="shop-page">
      {appMode ? (
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar onOpenQR={() => setQrOpen(true)} headerRightSlot={cartButton} />
      ) : (
        <PozzoTopNav rightSlot={cartButton} />
      )}

      {step === "menu" ? (
        <main className="max-w-5xl mx-auto px-4 py-6">
          {appMode && <SubPageNav hideDests lang={lang} />}
          <div className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-5">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold">{tr(T.shopHeroTitle, lang)}</h1>
            <p className="mt-1 text-sm text-[#f7ebdf]/85">{tr(T.shopHeroSubtitle, lang)}</p>
          </div>

          {menu.categories.length === 0 ? (
            <div className="mt-10 text-center text-[#7c5a4e]">
              <AlertCircle className="h-10 w-10 mx-auto mb-2" />
              <p>{tk("shopSoon")}</p>
            </div>
          ) : (
            <>
              {/* Sticky category filter (POIs-style chips) */}
              {menu.categories.length > 1 && (
                <div
                  data-testid="shop-category-filter"
                  className="sticky z-30 -mx-4 px-4 py-2 mt-4 bg-[#faf3ee]/95 backdrop-blur-md border-b border-[#e8dcc8] flex gap-2 overflow-x-auto no-scrollbar"
                  style={{ top: appMode ? "56px" : "0px" }}
                >
                  <ShopFilterChip
                    active={activeCategory === "all"}
                    onClick={() => setActiveCategory("all")}
                    label={`${tk("all")} · ${menu.categories.reduce((s, c) => s + c.items.length, 0)}`}
                    testId="shop-cat-all"
                  />
                  {menu.categories.map((c) => (
                    <ShopFilterChip
                      key={c.name}
                      active={activeCategory === c.name}
                      onClick={() => setActiveCategory(c.name)}
                      label={`${c.name} · ${c.items.length}`}
                      testId={`shop-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </div>
              )}
              {menu.categories
                .filter((cat) => activeCategory === "all" || cat.name === activeCategory)
                .map((cat) => {
              const isWine = ["vino", "vini"].includes((cat.name || "").toLowerCase());
              return (
              <section key={cat.name} className="mt-8">
                <h2 className="font-serif text-2xl font-bold text-[#2b1410] border-b border-[#e8dcc8] pb-2 mb-2">{cat.name}</h2>
                {isWine && (
                  <div
                    data-testid="wine-category-note"
                    className="mb-4 rounded-xl bg-[#faf3ee] border border-[#e8dcc8] px-4 py-3 text-[13px] text-[#2b1410] leading-relaxed"
                  >
                    <div className="font-semibold mb-1">🍷 {tk("wineBox")}</div>
                    <p className="text-[#5a3a2f]">
                      {tk("wineCaseInfo1")} <strong>{tk("wineCaseInfo2")}</strong>{tk("wineCaseInfo3")}
                    </p>
                    {wineQty > 0 && (
                      <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ${wineQty >= 6 ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                        {wineQty >= 6
                          ? `✓ ${wineQty} ${tk("bottlesInCart")}`
                          : `${wineQty}/6 · ${tk("addOther")} ${6 - wineQty}`}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cat.items.map((it) => (
                    <ProductCard key={it.id} item={it} qty={cart[it.id]?.qty || 0} onAdd={() => addToCart(it)} onDec={() => decItem(it.id)} onZoom={() => setZoom(it)} />
                  ))}
                </div>
              </section>
              );
            })}
            </>
          )}
        </main>
      ) : (
        <main className="max-w-2xl mx-auto px-4 py-6">
          <form onSubmit={submit} className="space-y-4">
            {cartLines.length === 0 ? (
              <div className="rounded-xl bg-white border border-[#e8dcc8] p-6 text-center text-[#7c5a4e]">
                {tk("emptyCartShop")} <button type="button" onClick={() => setStep("menu")} className="underline text-[#4a1c17]">{tk("backToShop")}</button>
              </div>
            ) : (
              <>
                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4">
                  <h3 className="font-serif text-lg font-bold text-[#2b1410] mb-2">Il tuo ordine</h3>
                  {cartLines.map((l) => (
                    <div key={l.item.id} className="py-2 flex items-center gap-3 border-t border-[#e8dcc8] first:border-t-0">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#2b1410] truncate">{l.item.name}</div>
                        <div className="text-[11px] text-[#7c5a4e]">{eur(l.item.price)} × {l.qty}{l.item.weight_kg > 0 && ` · ${l.item.weight_kg}kg`}</div>
                      </div>
                      <div className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-1.5 py-0.5">
                        <button type="button" onClick={() => decItem(l.item.id)} className="h-5 w-5 grid place-items-center"><Minus className="h-3 w-3" /></button>
                        <span className="text-xs w-5 text-center">{l.qty}</span>
                        <button type="button" onClick={() => addToCart(l.item)} className="h-5 w-5 grid place-items-center"><Plus className="h-3 w-3" /></button>
                      </div>
                      <div className="w-14 text-right text-sm font-semibold">{eur(l.item.price * l.qty)}</div>
                      <button type="button" onClick={() => remove(l.item.id)}><Trash2 className="h-3.5 w-3.5 text-[#7c5a4e]" /></button>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 grid sm:grid-cols-2 gap-3">
                  <Field label={`${tk("name")} *`}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sh-input" /></Field>
                  <Field label={`${tk("phone")} *`}><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" className="sh-input" /></Field>
                  <Field label={tk("emailOpt")}><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sh-input" /></Field>
                  <Field label={tk("notes")}><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sh-input" /></Field>
                </div>

                <ModeSelector form={form} setForm={setForm} anyNoShip={anyNoShip} allNoShip={allNoShip} subtotal={subtotal} menu={menu} totalWeight={totalWeight} />

                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 grid grid-cols-2 gap-2">
                  <ModeBtn active={form.payment_method === "stripe"} onClick={() => setForm({ ...form, payment_method: "stripe" })} icon={CreditCard} label={tk("card")} hint={tk("cardHint")} testId="shop-pay-stripe" />
                  <ModeBtn active={form.payment_method === "paypal"} onClick={() => setForm({ ...form, payment_method: "paypal" })} icon={Wallet} label="PayPal" hint={tk("paypalHint")} testId="shop-pay-paypal" />
                  <ModeBtn active={form.payment_method === "cash"} onClick={() => setForm({ ...form, payment_method: "cash" })} icon={Wallet} label={tk("cash")} hint={form.mode === "pickup" ? tk("atPickup") : tk("atDelivery")} testId="shop-pay-cash" />
                  {form.mode === "pickup" && (
                    <ModeBtn active={form.payment_method === "pos"} onClick={() => setForm({ ...form, payment_method: "pos" })} icon={CreditCard} label="POS" hint={tk("atPickup")} testId="shop-pay-pos" />
                  )}
                </div>

                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-1 text-sm">
                  <Row label={tk("subtotal")} value={eur(subtotal)} />
                  {form.mode !== "pickup" && (
                    <Row label={shippingFee > 0 ? (form.mode === "shipping" ? tk("shipping") : tk("delivery")) : tk("freeShipping")} value={eur(shippingFee)} />
                  )}
                  <Row label={tk("total")} value={eur(total)} bold />
                </div>

                <button type="submit" disabled={submitting || !form.name || !form.phone}
                  data-testid="shop-submit"
                  className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-3 font-semibold disabled:opacity-50">
                  {submitting ? <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> : null}
                  {form.payment_method === "stripe" ? `${tk("pay")} € ${total.toFixed(2)}` : `${tk("confirm")} · € ${total.toFixed(2)}`}
                </button>
                <ConsentNote action="Ordinando" />
              </>
            )}
          </form>
        </main>
      )}
      {zoom && (
        <ProductModal
          item={zoom}
          qty={cart[zoom.id]?.qty || 0}
          onClose={() => setZoom(null)}
          onAdd={() => addToCart(zoom)}
          onDec={() => decItem(zoom.id)}
        />
      )}
      {paypalPay && (
        <PayPalPayModal
          kind="shop"
          orderId={paypalPay.orderId}
          amount={paypalPay.amount}
          onSuccess={() => {
            const oid = paypalPay.orderId;
            setPaypalPay(null);
            nav(`/shop/success?order_id=${oid}&mode=paypal`);
          }}
          onClose={() => setPaypalPay(null)}
        />
      )}
      {step === "menu" && <AppBottomBar current="shop" lang={lang} spacer={!appMode} />}
      <QRDialog open={qrOpen} onOpenChange={setQrOpen} lang={lang} restaurant={restaurant} />
    </div>
  );
}

function ProductCard({ item, qty, onAdd, onDec, onZoom }) {
  const soldOut = item.stock === 0;
  const uniqueLeft = item.stock === 1 && qty === 0;
  const isQuadro = item.kind === "quadro";
  const imgCls = isQuadro
    ? "w-full aspect-video object-cover rounded-lg mb-2 bg-[#2b1410] cursor-zoom-in transition-transform active:scale-95"
    : "w-full h-40 object-cover rounded-lg mb-2 cursor-zoom-in transition-transform active:scale-95";
  const placeholderCls = isQuadro
    ? "w-full aspect-video rounded-lg bg-[#faf3ee] grid place-items-center text-[#7c5a4e] text-xs mb-2 cursor-zoom-in"
    : "w-full h-40 rounded-lg bg-[#faf3ee] grid place-items-center text-[#7c5a4e] text-xs mb-2 cursor-zoom-in";
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-3 flex flex-col" data-testid={`shop-item-${item.id}`}>
      {item.image ? (
        <img src={item.image} alt="" loading="lazy" decoding="async" className={imgCls} onClick={onZoom} data-testid={`shop-zoom-${item.id}`} />
      ) : (
        <div className={placeholderCls} onClick={onZoom} data-testid={`shop-zoom-${item.id}`}>FOTO</div>
      )}
      <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={onZoom}>
        <div className="font-semibold text-[#2b1410] leading-tight hover:underline">{item.name}</div>
        <div className="text-sm font-bold text-[#4a1c17] whitespace-nowrap">{eur(item.price)}</div>
      </div>
      {isQuadro && (item.art_type || item.dimensions) && (
        <div className="mt-1 text-[11px] text-[#7c5a4e] italic">
          {[item.art_type, item.dimensions].filter(Boolean).join(" · ")}
        </div>
      )}
      {item.description && (
        <p className="text-[12px] text-[#7c5a4e] mt-1 line-clamp-2 cursor-pointer" onClick={onZoom}>
          {item.description}
        </p>
      )}
      {item.description && item.description.length > 80 && (
        <button
          type="button"
          onClick={onZoom}
          className="mt-0.5 text-[11px] font-semibold text-[#4a1c17] hover:underline text-left"
          data-testid={`shop-more-${item.id}`}
        >
          {tk("readMore")}
        </button>
      )}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[10px] text-[#7c5a4e]">
          {isQuadro
            ? (tk("unique") + (uniqueLeft ? ` · ${tk("available")}` : ""))
            : (item.format || (item.weight_kg > 0 ? `${item.weight_kg} kg` : ""))}
        </div>
        {soldOut ? (
          <span className="text-xs text-red-600 font-semibold">{tk("soldOut")}</span>
        ) : qty > 0 ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-[#4a1c17] px-2 py-0.5">
            <button type="button" onClick={onDec} className="h-6 w-6 grid place-items-center text-[#4a1c17]"><Minus className="h-3.5 w-3.5" /></button>
            <span className="text-sm font-bold text-[#4a1c17] w-6 text-center">{qty}</span>
            <button type="button" onClick={onAdd} className="h-6 w-6 grid place-items-center text-[#4a1c17]"><Plus className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button type="button" onClick={onAdd} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1 text-xs font-semibold" data-testid={`shop-add-${item.id}`}>
            Aggiungi
          </button>
        )}
      </div>
    </div>
  );
}

function ModeSelector({ form, setForm, anyNoShip, allNoShip, subtotal, menu, totalWeight }) {
  const showShipping = !anyNoShip;
  const showLocal = allNoShip;
  const currentFee = form.mode === "shipping"
    ? (subtotal >= (menu.shipping?.free_threshold || 150) ? 0 : (totalWeight >= (menu.shipping?.heavy_kg_threshold || 5) ? menu.shipping?.heavy_fee : menu.shipping?.standard_fee))
    : null;
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <ModeBtn active={form.mode === "pickup"} onClick={() => setForm({ ...form, mode: "pickup" })} icon={Home} label={tk("pickup")} hint={tk("pickupHint")} testId="shop-mode-pickup" />
        {showShipping && (
          <ModeBtn active={form.mode === "shipping"} onClick={() => setForm({ ...form, mode: "shipping" })} icon={Truck} label={tk("shipping")} hint={currentFee === 0 ? tk("free") : (currentFee ? `€ ${currentFee}` : "8-15€")} testId="shop-mode-shipping" />
        )}
        {showLocal && (
          <ModeBtn active={form.mode === "local_delivery"} onClick={() => setForm({ ...form, mode: "local_delivery" })} icon={MapPin} label={tk("ourDelivery")} hint={tk("ourDeliveryHint")} testId="shop-mode-local" />
        )}
      </div>
      {(form.mode === "shipping" || form.mode === "local_delivery") && (
        <Field label={`${form.mode === "shipping" ? tk("shipAddress") : tk("deliveryAddress")} *`}>
          <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sh-input" placeholder="Via, numero civico, comune, CAP" />
        </Field>
      )}
      {anyNoShip && !allNoShip && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          Il tuo carrello contiene un quadro (non spedibile). Devi separare l'ordine, oppure svuotare i prodotti spedibili.
        </div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, hint, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-lg border-2 p-3 text-left transition ${active ? "border-[#4a1c17] bg-[#faf3ee]" : "border-[#e8dcc8] bg-white hover:border-[#4a1c17]/40"}`}>
      <Icon className={`h-4 w-4 ${active ? "text-[#4a1c17]" : "text-[#7c5a4e]"}`} />
      <div className="text-sm font-semibold mt-1">{label}</div>
      <div className="text-[11px] text-[#7c5a4e]">{hint}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (<label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">{label}<div className="mt-1">{children}</div></label>);
}

function Row({ label, value, bold }) {
  return <div className={`flex items-center justify-between ${bold ? "font-bold text-[#2b1410] text-base pt-1 border-t border-[#e8dcc8]" : "text-[#7c5a4e]"}`}><span>{label}</span><span>{value}</span></div>;
}

function ProductModal({ item, qty, onClose, onAdd, onDec }) {
  const isQuadro = item.kind === "quadro";
  const soldOut = item.stock === 0;
  // Close on Escape key.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="shop-product-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[95vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              loading="lazy"
              decoding="async"
              className={isQuadro ? "w-full aspect-video object-cover bg-[#2b1410]" : "w-full aspect-square object-cover"}
            />
          ) : (
            <div className={`${isQuadro ? "aspect-video" : "aspect-square"} bg-[#faf3ee] grid place-items-center text-[#7c5a4e]`}>{tk("noPhoto")}</div>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="shop-modal-close"
            className="absolute top-3 right-3 rounded-full bg-white/95 shadow-lg h-9 w-9 grid place-items-center hover:bg-white"
            aria-label={tk("close")}
          >
            <X className="h-4 w-4 text-[#2b1410]" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-serif text-2xl font-bold text-[#2b1410] leading-tight">{item.name}</h3>
            <div className="text-xl font-bold text-[#4a1c17] whitespace-nowrap">{eur(item.price)}</div>
          </div>
          <div className="text-[12px] uppercase tracking-wide text-[#7c5a4e]">
            {isQuadro
              ? [item.art_type, item.dimensions].filter(Boolean).join(" · ") || "Opera pezzo unico"
              : (item.format || (item.weight_kg ? `${item.weight_kg} kg` : ""))}
          </div>
          {item.description && (
            <p className="text-[14px] text-[#2b1410] leading-relaxed whitespace-pre-line">
              {item.description}
            </p>
          )}
          {isQuadro && (
            <div className="rounded-lg bg-[#faf3ee] border border-[#e8dcc8] px-3 py-2 text-[12px] text-[#5a3a2f]">
              🎨 <strong>Pezzo unico</strong> · una volta venduto non è più disponibile.<br />
              {tk("quadroDelivery")}
            </div>
          )}
          <div className="pt-2 flex items-center justify-between border-t border-[#e8dcc8]">
            <div className="text-[11px] text-[#7c5a4e]">
              {soldOut ? tk("notAvailable") : qty > 0 ? `${qty} ${tk("inCart")}` : tk("addToCart")}
            </div>
            {soldOut ? (
              <span className="text-sm text-red-600 font-semibold">{tk("soldOut")}</span>
            ) : qty > 0 ? (
              <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#4a1c17] px-2 py-1">
                <button type="button" onClick={onDec} data-testid="shop-modal-dec" className="h-8 w-8 grid place-items-center text-[#4a1c17] hover:bg-[#faf3ee] rounded-full"><Minus className="h-4 w-4" /></button>
                <span className="text-lg font-bold text-[#4a1c17] w-8 text-center">{qty}</span>
                <button type="button" onClick={onAdd} data-testid="shop-modal-inc" className="h-8 w-8 grid place-items-center text-[#4a1c17] hover:bg-[#faf3ee] rounded-full"><Plus className="h-4 w-4" /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onAdd}
                data-testid="shop-modal-add"
                className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-semibold"
              >
                <Plus className="h-4 w-4" /> {tk("add")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShopFilterChip({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
        active ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#2b1410] border-[#e8dcc8] hover:border-[#4a1c17]/40"
      }`}
    >
      {label}
    </button>
  );
}

const _sty = `.sh-input { display:block; width:100%; padding:.5rem .75rem; border-radius:.375rem; border:1px solid #e8dcc8; background:white; font-size:.875rem; outline:none; } .sh-input:focus { box-shadow:0 0 0 2px rgba(74,28,23,.25); border-color:#4a1c17; }`;
if (typeof document !== "undefined" && !document.getElementById("shop-style")) {
  const s = document.createElement("style"); s.id = "shop-style"; s.textContent = _sty; document.head.appendChild(s);
}
